import {
  toSeoProps,
  type UmbrellaSeoRoutes,
} from '@saas-pages/shared/public/seo'
import { buildUtilitySeoRoutes, type UtilityAppRegistry } from '@saas-pages/shared/public/platform-legacy'
import type { SeoProps } from '@saas-pages/shared/public/seo'
import branding from '../../branding.config'
import { enTranslations } from '@/shared/i18n/en'
import { ruTranslations, type Translations } from '@/shared/i18n/ru'
import { buildTools, type ToolDefinition, type ToolKey } from '@/shared/tools/tools'

export type StaticRouteKey = 'index' | 'blog'
export type AvailableToolKey = Extract<
  ToolKey,
  | 'textCounter'
  | 'textCase'
  | 'dedupeLines'
  | 'textCleanup'
  | 'transliteration'
  | 'fancyText'
  | 'passwordGenerator'
  | 'seoLimits'
>
export type RouteKey = StaticRouteKey | AvailableToolKey

export function buildSeoRoutes(t: Translations): UmbrellaSeoRoutes<RouteKey> {
  const tools = buildTools(t)
  const registry = {
    domain: branding.domain,
    staticRoutes: [
      {
        key: 'index',
        path: '/',
        seo: {
          path: '/',
          title: t.seo.indexTitle,
          description: t.seo.indexDescription,
          changefreq: 'weekly',
          priority: 1,
          jsonLd: {
            mode: 'none',
            reason: 'Catalog page; concrete utility pages carry tool JSON-LD.',
          },
        },
      },
      {
        key: 'blog',
        path: '/blog',
        seo: {
          path: '/blog',
          title: t.seo.blogTitle,
          description: t.seo.blogDescription,
          changefreq: 'weekly',
          priority: 0.8,
          jsonLd: {
            mode: 'none',
            reason: 'Blog index page; article pages own their article JSON-LD.',
          },
        },
      },
    ],
    tools: tools.filter(
      (tool): tool is ToolDefinition & { key: AvailableToolKey } =>
        tool.status === 'available',
    ),
  } satisfies UtilityAppRegistry<StaticRouteKey, ToolDefinition & { key: AvailableToolKey }>

  return buildUtilitySeoRoutes(registry)
}

export const seoRoutes: UmbrellaSeoRoutes<RouteKey> = buildSeoRoutes(ruTranslations)

export function seoFor(key: RouteKey, t: Translations = ruTranslations): SeoProps {
  const routes = buildSeoRoutes(t)
  return toSeoProps(routes.domain, routes.routes[key], {
    locale: t === enTranslations ? 'en' : 'ru',
  })
}
